import { useState, useMemo } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { Copy, Download } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { copyToClipboard, download } from '@/lib/utils';
import { toast } from 'sonner';

const SAMPLE = `# Hello, Utility OS

A **comprehensive** Markdown previewer with secure HTML output.

## Features

- Real-time preview
- GFM tables
- Code blocks with syntax highlight (mono)
- Sanitized HTML

\`\`\`js
function greet(name) {
  return 'Hello, ' + name;
}
\`\`\`

> Markdown is the default format for thinking out loud.

| Tool | Status |
| --- | --- |
| Merge PDF | ready |
| Image resize | ready |
| Color palette | ready |
`;

export function MarkdownPreview() {
  const [md, setMd] = useState(SAMPLE);
  const [view, setView] = useState<'rendered' | 'html'>('rendered');

  const html = useMemo(() => {
    try {
      const raw = marked.parse(md, { gfm: true, breaks: false }) as string;
      return DOMPurify.sanitize(raw);
    } catch {
      return '';
    }
  }, [md]);

  return (
    <div>
      <ToolHeader
        toolId="markdown-preview"
        title="Markdown editor"
        description="Live Markdown preview. Sanitized HTML output, exportable as .md or .html."
        actions={
          <Button
            size="sm"
            variant="ghost"
            onClick={() => download(html, 'document.html', 'text/html')}
          >
            <Download size={11} />
            HTML
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="!p-0">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
            <span className="text-xs font-medium text-fg-muted">Markdown</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => download(md, 'document.md', 'text/markdown')}
            >
              <Download size={11} />
            </Button>
          </div>
          <Textarea
            mono
            value={md}
            onChange={(e) => setMd(e.target.value)}
            className="!border-0 !rounded-t-none min-h-[480px] !bg-transparent focus-visible:!ring-0"
          />
        </Card>

        <Card className="!p-0">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
            <Tabs value={view} onValueChange={(v) => setView(v as typeof view)}>
              <TabsList>
                <TabsTrigger value="rendered">Preview</TabsTrigger>
                <TabsTrigger value="html">HTML</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button
              size="sm"
              variant="ghost"
              onClick={async () => {
                const ok = await copyToClipboard(view === 'html' ? html : md);
                if (ok) toast.success('Copied');
              }}
            >
              <Copy size={11} />
            </Button>
          </div>
          {view === 'rendered' ? (
            <div
              className="prose-uos p-5 overflow-auto min-h-[480px] max-h-[600px]"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          ) : (
            <pre className="p-5 font-mono text-[12px] leading-relaxed overflow-auto min-h-[480px] max-h-[600px]">
              <code>{html}</code>
            </pre>
          )}
        </Card>
      </div>
    </div>
  );
}

export default MarkdownPreview;
